import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { FormControl, Select, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import Loader from "layout/Loader";
import { CheckRTL } from "Helpers/CheckRTL";
import { truncDynCell } from "common/Table/truncDynCell";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import {
  Control,
  Controller,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { selectUserInfo } from "features/user/userSlice";
interface Column {
  id: string;
  label: string;
  numeric: boolean;
}

interface TableWithPaginationProps {
  columns: Column[];
  data: any;
  showHeader: boolean;
  showSearch: boolean;
  showFilter: boolean;
  tableData?: any;
  headerComponent?: React.ReactNode;
  handleChangePage: (field: number) => void;
  handleChangeRows: (field: number) => void;
  pageNo: number;
  pageSize: number;
  rowClick?: boolean;
  showRowView?: (row: any) => void;
  loadingSearch?: boolean;
  type: "jobCard" | "workOrder" | "invoice";
  showCheckbox?: boolean;
  form?: {
    control: Control<FieldValues, any>;
    watch: UseFormWatch<FieldValues>;
    setValue: UseFormSetValue<FieldValues>;
  };
}

const TableWithPagination: React.FC<TableWithPaginationProps> = ({
  columns,
  data,
  showHeader,
  showSearch,
  headerComponent,
  handleChangePage,
  handleChangeRows,
  tableData,
  pageSize,
  rowClick,
  showRowView,
  loadingSearch,
  type,
  showCheckbox,
  form,
}) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const { t, i18n } = useTranslation();
  const isRTL = CheckRTL();
  const loggedInUserData = useSelector(selectUserInfo);
  const handleChangePage1 = (action: string) => {
    if (tableData) {
      if (action === "first") {
        if (tableData?.pageable?.pageNumber > 0) {
          handleChangePage(0);
        }
      } else if (action === "prev") {
        if (tableData.pageable && tableData?.pageable?.pageNumber > 0) {
          handleChangePage(tableData?.pageable?.pageNumber - 1);
        }
      } else if (action === "next") {
        if (!tableData?.last) {
          handleChangePage(tableData?.pageable?.pageNumber + 1);
        }
      } else if (action === "last") {
        if (!tableData?.last) {
          handleChangePage(tableData?.totalPages - 1);
        }
      }
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    handleChangeRows(parseInt(event.target.value));
    setPage(0);
  };

  const visibleRows = useMemo(() => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [data, page, rowsPerPage]);
  return (
    <Box className="w-[100%] ">
      <Paper
        className="w-[100%] mb-[2]"
        sx={{ boxShadow: "none", background: themeColors?.appBg }}
      >
        {showHeader && showSearch ? (
          <Box
            className="table-header-toolbar"
            sx={{
              // minHeight: "56px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              width: "100%",
              margin: "5px 0px",
              gap: 1,
              "& > .MuiGrid-container, & > .table-header-search": {
                flex: "1 1 360px",
                width: "auto !important",
                maxWidth: "100%",
                minWidth: 0,
                alignSelf: "flex-start",
              },
              "& > .MuiGrid-item": {
                flex: "0 0 auto",
                width: "auto !important",
                maxWidth: "none",
                marginInlineStart: "auto",
                alignSelf: "flex-start",
              },
              "& > .MuiGrid-item ~ .MuiGrid-item": {
                marginInlineStart: 0,
              },
              "& > .MuiGrid-item ~ *:not(.MuiGrid-container):not(.MuiGrid-item):not(.table-header-search)":
                {
                  flex: "0 0 auto",
                },
              "& > .table-header-actions": {
                flex: "0 0 auto",
                marginInlineStart: "auto",
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "flex-start",
                alignSelf: "flex-start",
                justifyContent: "flex-end",
                gap: 1,
                "& .MuiButton-root": { marginInlineStart: 0 },
              },
            }}
          >
            {headerComponent}
          </Box>
        ) : null}
        {loadingSearch ? (
          <Loader />
        ) : (
          <>
            <TableContainer
              sx={{
                borderRadius: "8px",
                // marginTop: "10px",
                overflow: "visible",
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{ backgroundColor: "#fff" }}
                  className="sticky top-[42px] z-[10]"
                >
                  <TableRow>
                    {showCheckbox && <TableCell padding="checkbox"></TableCell>}
                    {columns.map((column, columnIndex) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          textAlign: isRTL
                            ? "right !important"
                            : "left !important",
                        }}
                        padding={column.id === "actions" ? "normal" : "normal"}
                        sortDirection={false}
                        className={`font-Saira ${columnIndex !== columns.length - 1 ? "borderRightApplied" : ""} ${column.id === "entityNo" ? "w-[150px]" : ""}`}
                      >
                        {
                          <span className="font-Saira font-semibold text-[13px] text-[#212B36]">
                            {t(column.label)}
                          </span>
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        align="center"
                        className="py-3"
                      >
                        <Typography
                          className="font-Saira font-[500] text-[15px] italic"
                          sx={{ color: themeColors?.primary }}
                        >
                          {t("noRecordFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row: any, index: number) => {
                      const displayNumber =
                        type === "jobCard"
                          ? row?.jobCardNo || row?.estimationNo
                          : row[`${type}No`];
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={false}
                          tabIndex={-1}
                          key={index}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? themeColors?.appBg : "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {showCheckbox && (
                            <TableCell padding="checkbox">
                              {row.invoiceStatus === 2 && (
                                <Controller
                                  name={`deliveryNoteIds${[row.id]}`}
                                  control={form?.control}
                                  defaultValue={false}
                                  render={({ field }) => (
                                    <Box
                                      sx={{
                                        "& .MuiCheckbox-root": {
                                          padding: "0 !important",
                                        },
                                        "& label": { padding: "0 !important" },
                                      }}
                                    >
                                      <CommonCheckbox
                                        {...field}
                                        label={""}
                                        onChange={(event) => {
                                          const isChecked =
                                            event.target.checked;
                                          field.onChange(isChecked);

                                          const currentSelected =
                                            form?.watch("deliveryNoteIds") ||
                                            [];
                                          let updatedSelected;

                                          if (isChecked) {
                                            updatedSelected =
                                              currentSelected.includes(row.id)
                                                ? currentSelected
                                                : [...currentSelected, row.id];
                                          } else {
                                            updatedSelected =
                                              currentSelected.filter(
                                                (id: number) => id !== row.id,
                                              );
                                          }

                                          form?.setValue(
                                            "deliveryNoteIds",
                                            updatedSelected,
                                          );
                                        }}
                                        checked={form?.watch(
                                          `deliveryNoteIds${[row.id]}`,
                                        )}
                                        color={themeColors?.primary}
                                      />
                                    </Box>
                                  )}
                                />
                              )}
                            </TableCell>
                          )}
                          {columns.map((column, columnIndex) => (
                            <TableCell
                              onClick={() => {
                                rowClick ? showRowView?.(row) : "";
                              }}
                              className={`font-Saira font-[400] text-[13px] ${columnIndex !== columns.length - 1 ? "borderRightApplied" : ""} ${isRTL ? "text-right" : "text-left"} ${column.id === "entityNo" ? "w-[150px]" : ""}`}
                              padding={"normal"}
                              align={"left"}
                            >
                              {column.id === "image" ? (
                                <img
                                  className="max-w-[48px] max-h-[48px] object-contain"
                                  src={
                                    row?.attributes?.makeTitle
                                      ? `/vehicle-optimized-images/${row?.attributes?.makeTitle
                                          .toLowerCase()
                                          .replace(/\s+/g, "-")}.png`
                                      : "/truck.png"
                                  }
                                  onError={(e) =>
                                    (e.currentTarget.src = "/truck.png")
                                  }
                                />
                              ) : column.id === "vehicleDetail" ? (
                                row?.attributes?.makeTitle ? (
                                  truncDynCell(
                                    `${row?.attributes?.makeTitle}-${row?.attributes?.modelTitle}${row?.attributes?.year ? "-" + row?.attributes?.year : ""}`,
                                  )
                                ) : (
                                  ""
                                )
                              ) : column.id === "plate" ? (
                                type === "invoice" ? (
                                  row?.vehiclePlateNo ? (
                                    truncDynCell(row?.vehiclePlateNo)
                                  ) : row?.invoiceType === 20 ? (
                                    t("directSales")
                                  ) : row?.invoiceType === 50 ? (
                                    t("consolidated")
                                  ) : (
                                    ""
                                  )
                                ) : (
                                  truncDynCell(row?.vehiclePlateNo || "")
                                )
                              ) : column.id === "entityNo" ? (
                                truncDynCell(displayNumber)
                              ) : column.id === "customerName" ? (
                                i18n?.language === "ar" &&
                                loggedInUserData?.business
                                  ?.secondaryLanguage === "Arabic" &&
                                (row?.invoiceType === 60
                                  ? row?.attributes?.companySecondaryName
                                  : row?.attributes?.customerSecondaryName ||
                                    row?.attributes?.companySecondaryName) ? (
                                  truncDynCell(
                                    row?.invoiceType === 60
                                      ? row?.attributes?.companySecondaryName ||
                                          ""
                                      : row?.attributes
                                          ?.customerSecondaryName ||
                                          row?.attributes
                                            ?.companySecondaryName ||
                                          "",
                                  )
                                ) : (
                                  truncDynCell(
                                    row?.invoiceType === 60
                                      ? row?.attributes?.companyName || ""
                                      : row?.attributes?.customerName ||
                                          row?.attributes?.companyName ||
                                          "",
                                  )
                                )
                              ) : column.id === "customerPhone" ? (
                                row?.attributes?.customerPhone || ""
                              ) : column.id === "invoiceType" ? (
                                [
                                  { value: 10, label: t("normal") },
                                  { value: 20, label: t("counterSale") },
                                  { value: 30, label: t("warranty") },
                                  { value: 40, label: t("excelImport") },
                                  { value: 50, label: t("consolidated") },
                                  { value: 60, label: t("insurance") },
                                ].find(
                                  (option: any) =>
                                    option.value === row[column.id],
                                )?.label
                              ) : column.id === "active" ? (
                                <Box
                                  className={
                                    (row?.active
                                      ? "status-active"
                                      : row.status === 203 || row.status === 303
                                        ? "status-inactive"
                                        : "status-cancelled") +
                                    " px-3 py-1 rounded-lg"
                                  }
                                >
                                  <Typography className="text-[12px] text-center">
                                    {row?.active
                                      ? t("open")
                                      : row.status === 203 || row.status === 303
                                        ? t("closed")
                                        : t("cancelled")}
                                  </Typography>
                                </Box>
                              ) : column.id === "vehicleVin" ? (
                                truncDynCell(row?.vehicleVin || "")
                              ) : column.id === "invoiceStatus" ? (
                                <span
                                  className={`${row?.paymentStatus === 777 ? "unpaid" : row?.paymentStatus === 778 ? "partialpaid" : "paid"} px-2 py-1 rounded-lg block text-center`}
                                >
                                  {row?.paymentStatus === 777
                                    ? t("unpaid")
                                    : row?.paymentStatus === 778
                                      ? t("partialPaid")
                                      : t("paid")}
                                </span>
                              ) : column.id === "invoiceStatusDeliveryNote" ? (
                                <span
                                  className={`${row?.invoiceStatus === 2 ? "unpaid" : "paid"} px-2 py-1 rounded-lg block text-center`}
                                >
                                  {row?.invoiceStatus === 2
                                    ? t("pending")
                                    : t("invoiced")}
                                </span>
                              ) : column.id === "stationName" ? (
                                i18n?.language === "ar" &&
                                loggedInUserData?.business
                                  ?.secondaryLanguage === "Arabic" &&
                                row?.attributes?.stationSecondaryName ? (
                                  truncDynCell(
                                    row?.attributes?.stationSecondaryName,
                                  )
                                ) : (
                                  truncDynCell(row?.attributes?.stationName)
                                )
                              ) : column.id === "createdAt" ? (
                                (() => {
                                  const date = new Date(row?.createdAt);
                                  return isNaN(date.getTime())
                                    ? "-"
                                    : new Intl.DateTimeFormat(i18n.language, {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                        // hour: "2-digit",
                                        // minute: "2-digit",
                                        // second: "2-digit",
                                        numberingSystem:
                                          i18n.language === "ur" ||
                                          i18n.language === "ar"
                                            ? "arab"
                                            : "latn",
                                      }).format(date);
                                })()
                              ) : (
                                truncDynCell(t(row[column.id]))
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <div
              style={{
                padding: "0 20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span className="hidden-xs mr-2 font-Saira font-[400] text-[13px]">
                {t("rowsPerPage")}
              </span>
              <span style={{ marginRight: 10, minWidth: 60 }}>
                {/* <strong>{tableData && tableData.pageable.pageSize}</strong>{" "} */}

                <FormControl className="m-1 min-w-[70]" size="small">
                  <Select
                    className="font-Saira font-[500] text-[13px] h-[25px] mt-[2px]"
                    value={
                      (tableData && tableData?.pageable?.pageSize) || pageSize
                    }
                    // value={rowsPerPageOptions[2]}
                    onChange={(event) =>
                      handleChangeRowsPerPage(
                        event as React.ChangeEvent<HTMLInputElement>,
                      )
                    }
                    // style={{height:"30px"}}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <MenuItem
                        className="font-Saira font-[500] text-[13px]"
                        key={option}
                        value={option}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </span>
              <span style={{ flex: 1 }}></span>

              <span
                // style={{ marginRight: 5, whiteSpace: "nowrap", maxWidth: 300 }}
                className="mr-2 whitespace-nowrap max-w-[300] font-Saira font-[400] text-[13px]"
              >
                {t("page")}
                <strong className="font-Saira ml-2 font-[700] text-[14px]">
                  {tableData && tableData.pageable
                    ? tableData.pageable.pageNumber + 1
                    : 1}
                </strong>{" "}
                <span className="visible-responsive"></span>
              </span>
              <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
                {t("outOf")}

                <strong className="ml-2 font-Saira font-[700] text-[14px]">
                  {tableData && tableData.totalPages ? tableData.totalPages : 1}
                </strong>
              </span>
              <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
                {t("totalRecords")}
                {/* )}{" "}  */}
                <strong className="ml-2 font-Saira font-[700] text-[14px]">
                  {tableData && tableData.totalElements
                    ? tableData.totalElements
                    : 0}
                </strong>
              </span>
              <span className="mr-2">
                <FirstPageIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("first")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
              <span className="mr-2">
                <NavigateBeforeIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("prev")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
              <span className="mr-2">
                <NavigateNextIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("next")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
              <span className="mr-2">
                <LastPageIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("last")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
            </div>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default TableWithPagination;
const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 100];
